"use client";

import React, { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";

const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+=<>/";
const STATIC_CHARS = new Set([" ", "·", ",", ".", "&"]);

function randomGlyph() {
  return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
}

// Resolves monospace labels left to right from random glyphs, once, when in view (or on mount).
export default function ScrambleText({
  text,
  className,
  delay = 0,
  duration = 700,
  trigger = "inView",
}: {
  text: string;
  className?: string;
  delay?: number;
  duration?: number;
  trigger?: "inView" | "mount";
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const reduceMotion = useReducedMotion();
  const [output, setOutput] = useState(text);
  const active = trigger === "mount" || inView;

  useEffect(() => {
    if (!active || reduceMotion) {
      setOutput(text);
      return;
    }

    const chars = Array.from(text);
    let frame = 0;
    let start: number | null = null;

    const tick = (now: number) => {
      start ??= now;
      const progress = Math.min(1, (now - start) / duration);
      const revealed = Math.floor(progress * chars.length);
      setOutput(chars.map((char, i) => (i < revealed || STATIC_CHARS.has(char) ? char : randomGlyph())).join(""));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    const timeout = window.setTimeout(() => {
      frame = requestAnimationFrame(tick);
    }, delay);

    return () => {
      window.clearTimeout(timeout);
      cancelAnimationFrame(frame);
    };
  }, [active, reduceMotion, text, delay, duration]);

  return (
    <span
      ref={ref}
      className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">{output}</span>
    </span>
  );
}
